const normalizeHexColor = (inputColor?: string): string | null => {
  if (!inputColor || typeof inputColor !== 'string') return null

  const sanitizedColorValue = inputColor.trim()
  const hexWithoutHash = sanitizedColorValue.startsWith('#')
    ? sanitizedColorValue.slice(1)
    : sanitizedColorValue

  if (!/^[0-9a-fA-F]+$/.test(hexWithoutHash)) return null

  if (hexWithoutHash.length === 3) {
    const expanded = hexWithoutHash
      .split('')
      .map((channel) => `${channel}${channel}`)
      .join('')
    return `#${expanded}`
  }

  if (hexWithoutHash.length === 6) return `#${hexWithoutHash}`
  if (hexWithoutHash.length === 8) return `#${hexWithoutHash.slice(0, 6)}`

  return null
}

const parseRgbFromCssColor = (inputColor?: string): { red: number; green: number; blue: number } | null => {
  if (!inputColor || typeof inputColor !== 'string') return null

  const rgbMatch = inputColor
    .trim()
    .match(/^rgba?\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\)$/i)

  if (!rgbMatch) return null

  const red = Number(rgbMatch[1])
  const green = Number(rgbMatch[2])
  const blue = Number(rgbMatch[3])

  if ([red, green, blue].some((channel) => Number.isNaN(channel) || channel < 0 || channel > 255)) {
    return null
  }

  return { red, green, blue }
}

const getPerceivedBrightness = (red: number, green: number, blue: number): number => {
  return red * 0.299 + green * 0.587 + blue * 0.114
}

const parseHexChannels = (hexColor: string): { red: number; green: number; blue: number } => {
  const red = Number.parseInt(hexColor.slice(1, 3), 16)
  const green = Number.parseInt(hexColor.slice(3, 5), 16)
  const blue = Number.parseInt(hexColor.slice(5, 7), 16)
  return { red, green, blue }
}

type TagTextColorOptions = {
  lightTextColor?: string
  darkTextColor?: string
  brightnessThreshold?: number
}

export const getTagTextColor = (
  backgroundColor?: string,
  options?: TagTextColorOptions,
): string => {
  const lightTextColor = options?.lightTextColor || '#FFFFFF'
  const darkTextColor = options?.darkTextColor || '#303030'
  const brightnessThreshold = options?.brightnessThreshold ?? 145

  const normalizedHexColor = normalizeHexColor(backgroundColor)
  const rgbFromHex = normalizedHexColor ? parseHexChannels(normalizedHexColor) : null
  const rgbColor = rgbFromHex || parseRgbFromCssColor(backgroundColor)

  if (!rgbColor) return darkTextColor

  const brightness = getPerceivedBrightness(rgbColor.red, rgbColor.green, rgbColor.blue)
  return brightness < brightnessThreshold ? lightTextColor : darkTextColor
}
